import { t } from "@lingui/core/macro";
import { i18n } from "@lingui/core";
import {
  GiftCardDocument,
  GiftCardReceiptDocument,
  GiftCardTransactionAccountDocument,
  InvoiceDocument,
  TransactionAccountReceiptDocument,
  PaymentType,
  ClosureDocument,
  PrinterConfig,
} from "./types";
import {
  buildHtmlQrCode,
  buildUpnHtmlQrCode,
  getCompanyNumberLabel,
  getPaddingCommands,
  getPaymentTypeLabel,
  getTaxIdentificationLabel,
} from "./utils";
import dayjs from "dayjs";
import {
  buildFinaVerificationUrl,
  buildUpnQrCodeString,
} from "../../../../shared/utils/tax-register";
import { formatCurrency } from "../../../../shared/utils/utils";
import DOMPurify from "dompurify";

export async function renderInvoiceToHtml({
  invoiceData: invoice,
  printerConfig,
  additionalSettings,
  isCopy,
}: {
  invoiceData: InvoiceDocument["invoiceData"];
  printerConfig: PrinterConfig;
  additionalSettings?: {
    receiptHeader?: string;
    receiptFooter?: string;
  };
  isCopy: boolean;
}): Promise<string> {
  if (!printerConfig) {
    throw new Error("Printer config is required");
  }

  const companyData = invoice.additionalReceiptData._documentIssuer;
  const companyInfo = `
    <div style="text-align: center">
      <span>${companyData.name}</span>
      <br />
      ${
        companyData.address
          ? `
        <span>${companyData.address}</span>
        <br />
      `
          : ``
      }
      ${companyData.zip ? `<span>${companyData.zip} ${companyData.city}</span>` : ``}
      <br />
      <span>${getTaxIdentificationLabel(invoice.provider, companyData.isTaxSubject)}: ${companyData.taxNumber || "/"}</span>
      <br />
      ${
        companyData.companyNumber
          ? `
        <span>${getCompanyNumberLabel(invoice.provider)}: ${companyData.companyNumber}</span>
        <br />
      `
          : ``
      }
    </div>
  `;

  const bpData = invoice.additionalReceiptData.businessPremiseData;
  const businessPremiseInfo = bpData
    ? `
    <div style="text-align: center">
      <span>${bpData.businessPremiseId}</span>
      <br />
      <span>${bpData.street} ${bpData.houseNumber ?? ""}${bpData.houseNumberAdditional ?? ""}</span>
      <br />
      <span>${bpData.postalCode ?? ""} ${bpData.city ?? ""}</span>
    </div>
  `
    : ``;

  const customerData = invoice.additionalReceiptData.customerData;
  const customerInfo = customerData
    ? `
    <div style="text-align: center">
      <span>${customerData.name}</span>
      <br />
      ${
        customerData.address
          ? `
        <span>${customerData.address}</span>
        <br />
      `
          : ``
      }
      ${customerData.zip && customerData.city ? `<span>${customerData.zip} ${customerData.city}</span>` : ``}
      <br />
      <span>ID za DDV: ${customerData.taxNumber || "/"}</span>
      <br />
    </div>
  `
    : ``;

  const invoiceHeader = `
    <div style="font-weight: bold; text-align: center;">
      ${invoice.canceled ? t`STORNIRAN RACUN` : ""}
      <br />
      ${t`RACUN ST.`}: ${invoice.number}
    </div>
  `;

  // 5. Items table
  //    Instead of spacing with ESC/POS commands, create a simple HTML table
  const itemsTableRows = invoice.additionalReceiptData.invoiceData.items
    .map((item) => {
      // First row: only the article name (spread across all columns)
      const row1 = `
      <tr>
        <td colspan="4" style="text-align: left;">${item.name}</td>
      </tr>
    `;

      // Second row: price, quantity, discount, amount
      const row2 = `
      <tr>
        <td style="text-align: right;">${item.price.toFixed(2)}</td>
        <td style="text-align: right;">${item.quantity}x</td>
        <td style="text-align: right;">${item.discount || ""}</td>
        <td style="text-align: right;">${item.totalWithTax.toFixed(2)}</td>
      </tr>
    `;

      return row1 + row2;
    })
    .join("");

  const itemsTable = `
  <table style="width: 100%; border-collapse: collapse; margin-top: 0.5em; border-bottom: 1px solid black;">
    <!-- Table header for the second row -->
    <thead style="font-weight: bold; border-bottom: 1px solid black;">
      <tr>
        <td>${t`Artikel/Cena`}</td>
        <td>${t`Kol`}</td>
        <td>${t`Popust`}</td>
        <td>${t`Vrednost`}</td>
      </tr>
    </thead>
    <tbody>
      ${itemsTableRows}
    </tbody>
  </table>
`;

  // 6. Totals section
  const totalsSection = `
    <div style="margin-top: 1em;">
      <div style="display: flex; justify-content: space-between;">
        <span>${t`SKUPAJ`}:</span>
        <span>${invoice.totalWithoutDiscount}</span>
      </div>
      ${
        invoice.totalDiscount
          ? `<div style="display: flex; justify-content: space-between;">
              <span>${t`POPUST`}:</span> 
              <span>-${invoice.totalDiscountFormatted}</span>
             </div>`
          : ``
      }
      <div style="display: flex; justify-content: space-between;">
        <span>${t`ZA PLACILO`} ${invoice.currencyId}:</span>
        <span style="font-size: 1.2em; font-weight: bold;">
          ${invoice.total}
        </span>
      </div>
    </div>
  `;

  const paymentMethodAmountsHTML = `
  <table style="width: 100%; border-collapse: collapse;">
    <tbody>
      ${invoice.additionalReceiptData.payments
        .map((payment) => {
          const label = getPaymentTypeLabel(payment.type as PaymentType);
          const formattedAmount = payment.amount.toFixed(2);

          return `
            <tr>
              <td style="text-align: left;">${label}</td>
              <td style="text-align: right;">${formattedAmount}</td>
            </tr>
          `;
        })
        .join("")}
    </tbody>
  </table>
`;

  let totalNetPrice = 0;
  let totalTaxAmount = 0;
  let totalGrossPrice = 0;

  const invoiceTaxesArray =
    invoice.additionalReceiptData.invoiceData.invoiceTaxes.map((tax) => {
      const netPrice = tax.base;
      const taxAmount = tax.totalTaxAmount;
      const grossPrice = netPrice + taxAmount;

      // Accumulate totals
      totalNetPrice += netPrice;
      totalTaxAmount += taxAmount;
      totalGrossPrice += grossPrice;

      return {
        taxRate: tax.taxRate.toFixed(1), // e.g. "22.0"
        netPrice: netPrice.toFixed(2),
        taxAmount: taxAmount.toFixed(2),
        grossPrice: grossPrice.toFixed(2),
      };
    });

  // Build the HTML table for taxes:
  function buildTaxHtmlTable(
    invoiceTaxes: {
      taxRate: string; // e.g. "22.0"
      netPrice: string; // e.g. "100.00"
      taxAmount: string; // e.g. "22.00"
      grossPrice: string; // e.g. "122.00"
    }[],
    totalNetPrice: number,
    totalTaxAmount: number,
    totalGrossPrice: number,
  ): string {
    // Map each tax line to a <tr> with right-aligned columns
    const taxLines = invoiceTaxes
      .map((t) => {
        return `
        <tr>
          <td style="text-align: right;">${t.taxRate}</td>
          <td style="text-align: right;">${t.netPrice}</td>
          <td style="text-align: right;">${t.taxAmount}</td>
          <td style="text-align: right;">${t.grossPrice}</td>
        </tr>
      `;
      })
      .join("");

    // Totals row
    const totalsRow = `
    <tr style="font-weight: bold; border-top: 1px solid #ccc;">
      <td style="text-align: left;">${t`SKUPAJ`}:</td>
      <td style="text-align: right;">${totalNetPrice.toFixed(2)}</td>
      <td style="text-align: right;">${totalTaxAmount.toFixed(2)}</td>
      <td style="text-align: right;">${totalGrossPrice.toFixed(2)}</td>
    </tr>
  `;

    // Put it all in one table
    return `
    <table style="width: 100%; border-collapse: collapse; margin-top: 1em;">
      <thead style="border-bottom: 1px solid #ccc;">
        <tr>
          <th style="text-align: right;">${invoice.provider === "furs" ? `DDV %` : "PDV %"}</th>
          <th style="text-align: right;">${t`Neto`}</th>
          <th style="text-align: right;">${invoice.provider === "furs" ? `DDV` : "PDV"}</th>
          <th style="text-align: right;">${t`Bruto`}</th>
        </tr>
      </thead>
      <tbody>
        ${taxLines}
        ${totalsRow}
      </tbody>
    </table>
  `;
  }

  const taxTableHtml = buildTaxHtmlTable(
    invoiceTaxesArray,
    totalNetPrice,
    totalTaxAmount,
    totalGrossPrice,
  );

  const includesMedicineService =
    invoice.additionalReceiptData.invoiceData.items.some(
      (item) => item.custom.isMedicineService,
    );

  // 7. Taxes & Additional Info
  const taxInfo = `
    <div style="margin-top: 1em;">
      ${taxTableHtml}

      <br />

      <p style="font-size: 0.85em;">${invoice.location.city ? invoice.location.city + ", " : ""}${invoice.date}</p>
      ${
        invoice.additionalReceiptData.employeeData?.name
          ? `<p style="font-size: 0.85em;">${t`Racun izdal`}: ${invoice.additionalReceiptData.employeeData.name}</p>`
          : ``
      }
      ${
        !companyData.isTaxSubject && invoice.provider === "furs"
          ? `<br />
            <p style="font-size: 0.85em; text-align: center;">
              DDV ni obracunan na podlagi 1. odstavka 94. clena ZDDV-1.
            </p>`
          : ``
      }
          ${
            !companyData.isTaxSubject && invoice.provider === "fina"
              ? `<br />
                <p style="font-size: 0.85em; text-align: center;">
                  PDV nije obračunat temeljem članka 90. stavak 1. i 2. Zakona o PDV-u.
                </p>`
              : ``
          }

      ${
        includesMedicineService &&
        invoice.provider === "furs" &&
        companyData.isTaxSubject
          ? `<br />
            <p style="font-size: 0.85em; text-align: center;">
              DDV ni obračunan v skladu z 2. točko 1. odstavka 42. člena ZDDV-1.
            </p>`
          : ``
      }

      ${
        includesMedicineService &&
        invoice.provider === "fina" &&
        companyData.isTaxSubject
          ? `<br />
            <p style="font-size: 0.85em; text-align: center;">
              PDV nije obračunat sukladno članku 39. stavak 1. točka b) Zakona o PDV-u.
            </p>`
          : ``
      }
          
      <br/>

      ${
        invoice.additionalReceiptData.ZOI != null
          ? `<p>ZOI: ${invoice.additionalReceiptData.ZOI || ""}</p>`
          : ``
      }
      ${
        invoice.additionalReceiptData.EOR != null
          ? `<p>EOR: ${invoice.additionalReceiptData.EOR || ""}</p>`
          : ``
      }
      ${
        invoice.additionalReceiptData.ZKI != null
          ? `<p>ZKI: ${invoice.additionalReceiptData.ZKI || ""}</p>`
          : ``
      }
      ${
        invoice.additionalReceiptData.JIR != null
          ? `<p>JIR: ${invoice.additionalReceiptData.JIR || ""}</p>`
          : ``
      }
    </div>
  `;

  // 8. Thank-you message & optional QR
  const qrCode = await (async () => {
    if (invoice.provider === "furs" && invoice.additionalReceiptData.QR) {
      return await buildHtmlQrCode(invoice.additionalReceiptData.QR);
    }

    if (invoice.provider === "fina") {
      const finaVerificationUrl = buildFinaVerificationUrl({
        jir: invoice.additionalReceiptData.JIR,
        zki: invoice.additionalReceiptData.ZKI,
        finaVerifyDate: invoice.finaVerifyDate,
        totalWithTax: invoice.totalWithTax,
      });

      if (finaVerificationUrl) {
        return await buildHtmlQrCode(finaVerificationUrl);
      }
    }

    return ``;
  })();

  const thanksMessage = `
    <div style="text-align: center; margin-top: 1em;">
      ${qrCode}
      <div>${t`Hvala za obisk!`}</div>
      <div>${t`www.lime-booking.si`}</div>
    </div>
  `;

  const printCount = invoice.additionalReceiptData.printCount + 1;

  const { paddingTopCommands, paddingBottomCommands } = getPaddingCommands({
    printerType: "star_mi",
    paddingBottom: printerConfig.paddingBottom,
    paddingTop: printerConfig.paddingTop,
  });

  const safe = (s?: string) =>
    s ? DOMPurify.sanitize(s, { USE_PROFILES: { html: true } }) : "";

  const receiptHeader = additionalSettings?.receiptHeader
    ? `<div style="text-align: center; margin-bottom: 1em">${safe(additionalSettings.receiptHeader)}</div>`
    : ``;

  const receiptFooter = additionalSettings?.receiptFooter
    ? `<br /><div style="text-align: center; margin-top: 1em">${safe(additionalSettings.receiptFooter)}</div><br />`
    : ``;

  return `
    <div style="width: 100%;">
      <style>
          :root {
            font-family: Arial;
          }

          p {
            margin: 0;
            font-size: 1em;
          }

          table, td {
            font-size: 1em;
          }

          span {
            font-size: 0.85em;
          }
        </style>

      <div style="width: 100%;">
        ${paddingTopCommands}

        ${receiptHeader}

        ${companyInfo}
        <br />
        ${businessPremiseInfo}
        <br />
        ${customerInfo}
        <br />
        ${isCopy && invoice.provider === "furs" ? `<p style="text-align: center;">Kopija ${printCount - 1}</p>` : ``}
        <br />
        ${invoiceHeader}
        <br />
        ${itemsTable}
        <br />
        ${totalsSection}
        ${paymentMethodAmountsHTML}
        <br />
        ${taxInfo}
        <br />
        ${thanksMessage}
        
        ${receiptFooter}
        
        ${paddingBottomCommands}  
        </div>
    </div>
  `;
}

export function renderGiftCardToHtml({
  giftCardData,
  printerConfig,
}: {
  giftCardData: GiftCardDocument;
  printerConfig: PrinterConfig;
}): string {
  if (!printerConfig) {
    throw new Error("Printer config is required");
  }

  const { organization, giftCard, operatorLabel } = giftCardData.data;

  const { paddingTopCommands, paddingBottomCommands } = getPaddingCommands({
    printerType: "star_mi",
    paddingBottom: printerConfig.paddingBottom,
    paddingTop: printerConfig.paddingTop,
  });

  return `
    <div style="text-align: center;">
      <style>
        p {
          margin: 0;
          font-size: 1em;
          font-family: Arial;
        }
        span {
          font-size: 0.85em;
          font-family: Arial;
        }
      </style>
      ${paddingTopCommands}
      <p>${organization.name}</p>
      <p>${organization.address}${organization.address2 || ""}, ${organization.zip} ${organization.city}</p>
      <p>${getTaxIdentificationLabel("furs", organization.isTaxSubject)}: ${organization.taxNumber}</p>
      ${organization.companyNumber ? `<p>${getCompanyNumberLabel("furs")}: ${organization.companyNumber}</p>` : ``}
      <br />
      <div style="text-align: left;">
        <p>${t`Blagajnik`}: ${operatorLabel}</p>
      </div>
      <br/>
      <div style="text-align: center; font-size: 1.75em;">
        <span>${t`DARILNI BON`}</span>
        <span>${giftCard.initialAmountFormatted}</span>
      </div>
      ${
        giftCard.amountLeftCents !== giftCard.initialAmountCents
          ? `
            <p>${t`Preostali znesek`}: ${giftCard.amountLeftFormatted}</p>
        `
          : ``
      }
      <br/>

      <p style="font-weight: bold;">${t`Koda`}<p>
      <p style="font-weight: bold;">${giftCard.code}</p>

      <br/>

      <div style="text-align: left;">
        <p>${t`Veljavnost od`}: ${giftCard.issueDate}</p>
        <p>${t`Veljavnost do`}: ${giftCard.expiryDate}</p>
      </div>
      <br />
      <span style="margin-bottom: 0;">${t`Hvala za obisk!`}</span>
      <br />
      <span style="margin-top: 0;">${t`www.lime-booking.si`}</span>
      ${paddingBottomCommands}
    </div>
  `;
}

export function renderGiftCardReceiptToHtml({
  receiptData,
  printerConfig,
}: {
  receiptData: GiftCardReceiptDocument;
  printerConfig: PrinterConfig;
}): string {
  if (!printerConfig) {
    throw new Error("Printer config is required");
  }

  const {
    organization,
    issuedGiftCards,
    operatorLabel,
    payments,
    dateTime,
    location,
  } = receiptData.data;

  const { paddingTopCommands, paddingBottomCommands } = getPaddingCommands({
    printerType: "star_mi",
    paddingBottom: printerConfig.paddingBottom,
    paddingTop: printerConfig.paddingTop,
  });

  return `
    <div style="text-align: center;">
      <style>
        p {
          margin: 0;
          font-size: 1em;
          font-family: Arial;
        }
        span {
          font-size: 0.85em;
          font-family: Arial;
        }
      </style>
      ${paddingTopCommands}
      <p>${organization.name}</p>
      <p>${organization.address}${organization.address2 || ""}, ${organization.zip} ${organization.city}</p>
      <p>${getTaxIdentificationLabel("furs", organization.isTaxSubject)}: ${organization.taxNumber}</p>
      ${organization.companyNumber ? `<p>${getCompanyNumberLabel("furs")}: ${organization.companyNumber}</p>` : ``}
      <br />
      <div style="text-align: left;">
        <p>${t`Blagajnik`}: ${operatorLabel}</p>
      </div>
      <br/>

      <div style="text-align: left;">
        <p style="border-bottom: 1px solid black;">${t`Prejeta plačilna sredstva`}</p>
        ${Object.entries(payments)
          .map(
            ([type, data]) => `
          <div style="display: flex; justify-content: space-between;">
            <span>${getPaymentTypeLabel(type as PaymentType)}</span>
            <span>${data.amountFormatted}</span>  
          </div>
        `,
          )
          .join("")}

        <br />

        <p style="border-bottom: 1px solid black;">${t`Izdana plačilna sredstva`}</p>
        ${issuedGiftCards
          .map(
            (g) => `<div style="display: flex; justify-content: space-between;">
                    <span>${t`DARILNI BON`}</span>
                    <span>${g.initialAmountFormatted}</span>  
                  </div>`,
          )
          .join("")}
      </div>

      <br/>
      <br/>

      <p>${location}, ${dateTime}</p>

      <br/>
      <br/>

      <span style="margin-bottom: 0;">${t`Hvala za obisk!`}</span>
      <br />
      <span style="margin-top: 0;">${t`www.lime-booking.si`}</span>
      ${paddingBottomCommands}
    </div>
  `;
}

export async function renderTransactionAccountReceiptToHtml({
  doc,
  printerConfig,
}: {
  doc: TransactionAccountReceiptDocument;
  printerConfig: PrinterConfig;
}): Promise<string> {
  if (!printerConfig) {
    throw new Error("Printer config is required");
  }

  const { data } = doc;

  const purpose = data.invoiceNumber;
  const htmlQrCode = await buildUpnHtmlQrCode(
    buildUpnQrCodeString({
      invoiceNumber: data.invoiceNumber,
      amount: data.amount,
      dueDate: dayjs(data.dateDue).format("DD.MM.YYYY"),
      paymentPurpose: purpose,
      purposeCode: "OTHR",
      recipientCity: data.city,
      recipientIban: data.IBAN,
      recipientName: data.name,
      recipientReference: data.reference,
      recipientStreet: data.address,
      payerCity: "",
      payerName: "",
      payerStreet: "",
    }),
    "80%",
  );

  const { locale } = i18n;

  const { paddingTopCommands, paddingBottomCommands } = getPaddingCommands({
    printerType: "star_mi",
    paddingBottom: printerConfig.paddingBottom,
    paddingTop: printerConfig.paddingTop,
  });

  return `
    <div style="text-align: center;">
      <style>
        p {
          margin: 0;
          font-size: 1em;
          font-family: Arial;
        }

        span {
          font-size: 0.85em;
          font-family: Arial;
        }
      </style>
      ${paddingTopCommands}
      <p>${t`Racun`}: ${data.invoiceNumber}</p>
      <p>${t`Znesek`}: ${data.amount.toFixed(2)} ${data.currency}</p>
      <p>${t`Ime`}: ${data.name}</p>
      <p>${t`Naslov`}: ${data.address} ${data.city} ${data.country}</p>
      <p>IBAN: ${data.IBAN}</p>
      <p>SWIFT: ${data.SWIFT}</p>
      <p>${t`Referenca`}: ${data.reference}</p>
      <p>${t`Namen`}: ${purpose}</p>
      <p>${t`Rok placila`}: ${dayjs(data.dateDue).toDate().toLocaleDateString(locale)}</p>

      <br />

      ${htmlQrCode}

      <br />
      <span style="margin-bottom: 0;">${t`Hvala za obisk!`}</span>
      <br />
      <span style="margin-top: 0;">${t`www.lime-booking.si`}</span>
      ${paddingBottomCommands}
    </div>
  `;
}

export async function renderGiftCardTransactionAccountToHtml({
  doc,
  printerConfig,
}: {
  doc: GiftCardTransactionAccountDocument;
  printerConfig: PrinterConfig;
}): Promise<string> {
  if (!printerConfig) {
    throw new Error("Printer config is required");
  }

  const { data } = doc;

  if (!data.organization.IBAN) {
    throw new Error("IBAN is required");
  }

  const purpose = data.giftCardInvoiceId;
  const htmlQrCode = await buildUpnHtmlQrCode(
    buildUpnQrCodeString({
      invoiceNumber: data.giftCardInvoiceId,
      amount: data.amount,
      dueDate: dayjs(data.dateDue).format("DD.MM.YYYY"),
      paymentPurpose: purpose,
      purposeCode: "OTHR",
      recipientCity: data.organization.city,
      recipientIban: data.organization.IBAN,
      recipientName: data.organization.name,
      recipientReference: data.reference,
      recipientStreet: data.organization.address,
      payerCity: "",
      payerName: "",
      payerStreet: "",
    }),
    "80%",
  );

  const { locale } = i18n;

  const { paddingTopCommands, paddingBottomCommands } = getPaddingCommands({
    printerType: "star_mi",
    paddingBottom: printerConfig.paddingBottom,
    paddingTop: printerConfig.paddingTop,
  });

  return `
    <div style="text-align: center;">
      <style>
        p {
          margin: 0;
          font-size: 1em;
          font-family: Arial;
        }

        span {
          font-size: 0.85em;
          font-family: Arial;
        }
      </style>
      ${paddingTopCommands}
      <p>${t`TRR plačilo za darilni bon`}</p>
      <p>${t`Znesek`}: ${data.amount.toFixed(2)} ${data.currency}</p>
      <p>${t`Ime`}: ${data.organization.name}</p>
      <p>${t`Naslov`}: ${data.organization.address} ${data.organization.city} ${data.organization.zip}</p>
      <p>IBAN: ${data.organization.IBAN}</p>
      <p>SWIFT: ${data.organization.SWIFT ?? "/"}</p>
      <p>${t`Referenca`}: ${data.reference}</p>
      <p>${t`Namen`}: ${purpose}</p>
      <p>${t`Rok placila`}: ${dayjs(data.dateDue).toDate().toLocaleDateString(locale)}</p>

      <br />

      ${htmlQrCode}

      <br />
      <span style="margin-bottom: 0;">${t`Hvala za obisk!`}</span>
      <br />
      <span style="margin-top: 0;">${t`www.lime-booking.si`}</span>
      ${paddingBottomCommands}
    </div>
  `;
}

export async function renderClosureToHtml({
  closureData,
  printerConfig,
}: {
  closureData: ClosureDocument;
  printerConfig: PrinterConfig;
}): Promise<string> {
  if (!printerConfig) {
    throw new Error("Printer config is required");
  }

  const { data } = closureData;

  // Header section
  const header = `
    <div style="text-align: center; font-weight: bold;">
      <p>${t`Datum otvoritve`}: ${data.openDate}</p>
      <p>${t`Datum zaključka`}: ${data.closeDate}</p>
    </div>
  `;

  // Payment types summary
  const paymentSummary = `
    <div style="text-align: left;">
      <p style="border-bottom: 1px solid black; font-weight: bold;">${t`Prejeta plačilna sredstva`}</p>
      ${Object.entries(data.amountByPaymentType)
        .flatMap(([type, amount]) =>
          amount.amount > 0
            ? [
                `
          <div style="display: flex; justify-content: space-between;">
            <span>${getPaymentTypeLabel(type as PaymentType)}:</span>
            <span>${amount.amountFormatted}</span>
          </div>
        `,
              ]
            : [],
        )
        .join("")}
    </div>
  `;

  // VAT summary
  const vatSummary = `
    <div style="text-align: left; margin-top: 10px;">
      <p style="border-bottom: 1px solid black; font-weight: bold;">${t`Povzetek DDV`}</p>
      ${Object.entries(data.amountByTaxRate)
        .flatMap(([rate, amount]) =>
          amount.taxAmount > 0
            ? [
                `
          <div style="display: flex; justify-content: space-between;">
            <span>${t`DDV`} ${rate}%:</span>
            <span>${amount.taxAmountFormatted}</span>
          </div>
        `,
              ]
            : [],
        )
        .join("")}
      <div style="display: flex; justify-content: space-between; font-weight: bold; border-top: 1px solid black; margin-top: 5px; padding-top: 5px;">
        <span>${t`DDV skupaj`}:</span>
        <span>${Object.values(data.amountByTaxRate)
          .reduce((sum, amount) => sum + (amount.taxAmount ?? 0), 0)
          .toFixed(2)}</span>
      </div>
    </div>
  `;

  // Final cash amount summary
  const finalCashAmount =
    data.amountByPaymentType.cash?.amountFormatted || "0.00";
  const cashSummary = `
    <div style="text-align: left; margin-top: 10px;">
      <div style="display: flex; justify-content: space-between; font-weight: bold;">
        <span>${t`Končni saldo blagajne`}:</span>
        <span>${finalCashAmount}</span>
      </div>
    </div>
  `;

  const issuedGiftCardsSummary =
    data.issuedGiftCardsByUser.length > 0
      ? `
    <div style="text-align: left; margin-top: 10px;">
      <p style="border-bottom: 1px solid black; font-weight: bold;">${t`Izdani darilni boni`}</p>
      ${data.issuedGiftCardsByUser
        .map(
          (user) => `
          <div style="margin-top: 8px;">
            <p style="font-weight: bold;">${user.name} ${user.lastName}</p>
            ${user.giftCards
              .map(
                (giftCard) => `
                <div style="display: flex; justify-content: space-between; margin-left: 16px;">
                  <span>${giftCard.code}</span>
                  <span>${giftCard.initialAmountFormatted}</span>
                </div>
                ${
                  giftCard.cashAmountFormatted
                    ? `
                  <div style="display: flex; justify-content: space-between; margin-left: 32px;">
                    <span>${getPaymentTypeLabel("cash")}:</span>
                    <span>${giftCard.cashAmountFormatted}</span>
                  </div>
                `
                    : ""
                }
                ${
                  giftCard.cardAmountFormatted
                    ? `
                  <div style="display: flex; justify-content: space-between; margin-left: 32px;">
                    <span>${getPaymentTypeLabel("card")}:</span>
                    <span>${giftCard.cardAmountFormatted}</span>
                  </div>
                `
                    : ""
                }
                ${
                  giftCard.onlineAmountFormatted
                    ? `
                  <div style="display: flex; justify-content: space-between; margin-left: 32px;">
                    <span>${getPaymentTypeLabel("online")}:</span>
                    <span>${giftCard.onlineAmountFormatted}</span>
                  </div>
                `
                    : ""
                }
              `,
              )
              .join("")}
            <div style="display: flex; justify-content: space-between; margin-left: 16px; font-weight: bold; margin-top: 4px;">
              <span>${t`Skupaj izdani darilni boni`}:</span>
              <span>${user.totalAmountFormatted}</span>
            </div>
          </div>
        `,
        )
        .join("")}
    </div>
  `
      : "";

  // Documents list header
  const documentsHeader = `
    <div style="margin-top: 15px;">
      <p style="border-bottom: 2px solid black; font-weight: bold; text-align: center;">Seznam računov</p>
      <div style="display: flex; justify-content: space-between; font-size: 0.8em; border-bottom: 1px solid black; padding: 2px 0;">
        <span>Št. računa</span>
        <span>Datum</span>
        <span>Skupaj</span>
      </div>
    </div>
  `;

  // Documents list
  const documentsList = data.documents
    .map((doc) => {
      const docDate = dayjs(doc.dateTime).format("DD.MM.YYYY HH:mm");
      const customerLine = doc.customerName
        ? `
      <div style="font-size: 0.9em; margin-left: 20px;">
        ${doc.customerName}
      </div>
    `
        : "";

      return `
      <div style="border-bottom: 1px dotted #ccc; padding: 2px 0;">
        <div style="display: flex; justify-content: space-between; font-size: 1em;">
          <span>${doc.number}</span>
          <span>${docDate}</span>
          <span>${doc.totalWithTaxFormatted}</span>
        </div>
        ${customerLine}
        ${doc.payments
          .map(
            (payment) => `
          <div style="font-size: 1em; margin-left: 20px;">
            ${getPaymentTypeLabel(payment.type)}: ${payment.amountFormatted}
          </div>
        `,
          )
          .join("")}
        ${
          doc.taxes.length > 0
            ? doc.taxes
                .map(
                  (tax) => `
          <div style="font-size: 1em; margin-left: 20px;">
            ${t`DDV`} ${tax.taxRate}%: ${tax.taxAmountFormatted}
          </div>
        `,
                )
                .join("")
            : ""
        }
      </div>
    `;
    })
    .join("");

  // Footer totals
  const totalAmount = data.documents.reduce(
    (sum, doc) => sum + doc.totalWithTax,
    0,
  );

  const totalAmountFormatted = formatCurrency({
    amount: totalAmount,
    currency: data.currency,
    fractionDigits: 2,
    locale: i18n.locale,
  });

  const { paddingTopCommands, paddingBottomCommands } = getPaddingCommands({
    printerType: "star_mi",
    paddingBottom: printerConfig.paddingBottom,
    paddingTop: printerConfig.paddingTop,
  });

  const footer = `
    <div style="margin-top: 15px; text-align: center;">
      <div style="border-top: 2px solid black; padding-top: 5px; font-weight: bold;">
        <div style="display: flex; justify-content: space-between;">
          <span>${t`Skupaj`}:</span>
          <span>${totalAmountFormatted}</span>
        </div>
      </div>
    </div>
  `;

  return `
    <div style="width: 100%;">
      <style>
        p {
          margin: 2px 0;
          font-size: 1em;
          font-family: Arial, sans-serif;
        }
        span {
          font-size: 0.9em;
          font-family: Arial, sans-serif;
        }
        div {
          margin: 1px 0;
        }
      </style>
      
      ${paddingTopCommands}
      ${header}
      <br />
      ${paymentSummary}
      <br />
      ${vatSummary}
      <br />
      ${cashSummary}
      ${issuedGiftCardsSummary ? `<br />${issuedGiftCardsSummary}` : ""}
      <br />
      ${documentsHeader}
      ${documentsList}
      ${footer}
      
      <div style="text-align: center; margin-top: 20px;">
        <span style="font-size: 0.8em;">*******************************************</span>
        <br />
        <span style="font-size: 0.8em;">${t`Hvala za obisk!`}</span>
        <br />
        <span style="font-size: 0.8em;">${t`www.lime-booking.si`}</span>
      </div>
      ${paddingBottomCommands}
    </div>
  `;
}
